import { useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquare } from "lucide-react";
import Button from "shared/Button";
import StatusPill from "shared/StatusPill";
import Alert from "shared/Alert";
import { showSuccess } from "shared/toast";
import { cn } from "lib/utils";
import { apiErrorMessage } from "../apiError";
import { getConversation, postMessageAnalysis, type Channel, type Conversation } from "../api/OutreachTouchesApi";
import { formatKarachi } from "../outreachDates";
import { announceDueChange } from "../modal/useDueCounts";
import ConversationActions, { OUTCOME_LABEL } from "./ConversationActions";
import { resultStatus, turnStatus } from "./conversationStatus";

export { OUTCOME_LABEL };

const CHANNEL_LABEL: Record<Channel, string> = { email: "Email", linkedin: "LinkedIn" };
const SOURCE_LABEL: Record<string, string> = { ui: "logged here", linkedin_touch: "from the LinkedIn step", cli: "logged by the command", backfill: "imported from the old reply log", touch_connection_request: "connection request note", touch_acceptance_message: "message after they accepted", touch_follow_up: "follow-up" };

interface Props {
  conversation: Conversation;
  /** Called after any change so the page reloads its lists. */
  onChanged: () => void;
}

export default function ConversationCard({ conversation, onChanged }: Props) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<Conversation | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const c = detail ?? conversation;

  async function load() {
    try { setDetail(await getConversation(conversation.id)); } catch (e) { setError(apiErrorMessage(e, "Could not load the thread.")); }
  }
  async function toggle() {
    const next = !open;
    setOpen(next);
    if (next) await load();
  }
  /** Run one change, show the fresh conversation, tell the page. */
  async function change<T extends Conversation | unknown>(fn: () => Promise<T>, done?: string): Promise<boolean> {
    setBusy(true);
    setError("");
    try {
      const result = await fn();
      if (done) showSuccess(done);
      announceDueChange();
      onChanged();
      if (open && (result as Conversation)?.messages) setDetail(result as Conversation); else if (open) await load();
      return true;
    } catch (e) {
      setError(apiErrorMessage(e, "Could not save that. Reload and try again."));
      return false;
    } finally {
      setBusy(false);
    }
  }

  const who = c.contact_name || c.contact_email || "Unknown contact";
  const turn = turnStatus(c);
  const result = resultStatus(c.outcome);
  return (
    <div data-testid={`conversation-${c.id}`} className="rounded-xl border border-border bg-surface p-3 space-y-2">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground truncate">
            <Link to={`${window.location.pathname.startsWith("/employee") ? "/employee" : "/admin"}/crm/companies/${c.prime_company_id}`} className="hover:underline">{c.company_name}</Link>
            <span className="font-normal text-muted-foreground"> · {who}</span>
          </p>
          <p className="text-xs text-muted-foreground truncate">
            <Link to={`${window.location.pathname.startsWith("/employee") ? "/employee" : "/admin"}/crm?opportunity_id=${c.opportunity_id}`} className="underline underline-offset-2 hover:text-foreground" title="Open the RFP details">{c.rfp_title}</Link>
            {c.contact_name && c.contact_email ? ` · ${c.contact_email}` : ""}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {turn && <StatusPill tone={turn.tone} size="sm" title={c.no_answer ? "We wrote and they have not answered. Write again, or close it." : undefined}>{turn.label}</StatusPill>}
          {result && <StatusPill tone={result.tone} size="sm">{result.label}</StatusPill>}
          {c.pending_analysis > 0 && <StatusPill tone="info" size="sm" title="Run /rfp-prime-reply-analysis to learn from this reply">Analysis pending</StatusPill>}
          {c.redirected_from_id && <StatusPill tone="neutral" size="sm">Redirected here</StatusPill>}
        </div>
      </div>

      {c.last_message_preview ? (
        <p className="text-sm text-foreground line-clamp-2 whitespace-pre-wrap">
          <span className="text-xs text-muted-foreground">{c.last_sender === "us" ? "We wrote" : "They wrote"}{c.last_channel ? ` on ${CHANNEL_LABEL[c.last_channel]}` : ""}{c.last_message_at ? `, ${formatKarachi(c.last_message_at)}` : ""}: </span>
          {c.last_message_preview}
        </p>
      ) : (
        <p className="text-sm text-muted-foreground">No message yet. Write to them, then log it here.</p>
      )}

      <div>
        <Button variant="outline" size="sm" onClick={toggle} aria-expanded={open}>
          <MessageSquare className="w-3.5 h-3.5 mr-1.5" aria-hidden />{open ? "Hide thread" : `Open thread (${c.message_count})`}
        </Button>
      </div>

      {error && <Alert tone="danger" role="alert">{error}</Alert>}

      {open && detail && (
        <div className="space-y-3 border-t border-border pt-3">
          <ol className="space-y-2" aria-label="Thread">
            {[...(detail.messages ?? [])].reverse().map((m) => (
              <li key={m.id} className={cn("rounded-lg border px-3 py-2 text-sm", m.sender === "us" ? "bg-primary-soft border-primary/30 ml-6" : "bg-surface-muted border-border mr-6")}>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground mb-1">
                  <span className="font-medium text-foreground">{m.sender === "us" ? "Us" : who}</span>
                  <StatusPill tone="neutral" size="sm">{CHANNEL_LABEL[m.channel]}</StatusPill>
                  <span>{formatKarachi(m.at)}</span>
                  <span>· {SOURCE_LABEL[m.source] ?? m.source}</span>
                  {m.sender === "contact" && m.analysis_status === "pending" && (
                    <>
                      <StatusPill tone="info" size="sm">Analysis pending</StatusPill>
                      <button type="button" disabled={busy} className="underline underline-offset-2 disabled:opacity-50"
                        onClick={() => void change(() => postMessageAnalysis(m.id, { status: "skipped", note: "Nothing to learn from this message" }), "Marked as nothing to analyse")}>
                        Nothing to analyse
                      </button>
                    </>
                  )}
                  {m.sender === "contact" && m.analysis_status === "needs_context" && <StatusPill tone="warning" size="sm">Needs more context</StatusPill>}
                  {m.sender === "contact" && m.analysis_status === "done" && <StatusPill tone="success" size="sm">Analysed</StatusPill>}
                  {m.sender === "contact" && m.analysis_status === "skipped" && <StatusPill tone="neutral" size="sm">Nothing to analyse</StatusPill>}
                </div>
                <p className="whitespace-pre-wrap">{m.text}</p>
                {m.analysis_note && <p className="mt-1 text-xs text-muted-foreground">{m.analysis_note}</p>}
              </li>
            ))}
          </ol>

          <ConversationActions conversation={c} onChanged={(updated, kind) => { onChanged(); if (updated && kind !== "redirect") setDetail(updated); else void load(); }} />
        </div>
      )}
    </div>
  );
}

